import {
  faCheckCircle,
  faCloud,
  faExclamationTriangle,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@repo/videa-components';

import { buttonStyling } from '../styles';
import type { AppointmentPatient } from '../types';
import type { ClinicalNote } from '../types/api';

import { TooltipButton } from './TooltipButton';

interface PMSNoteActionsProps {
  patient: AppointmentPatient | undefined;
  selectedNote: ClinicalNote;
  isSavingNoteToPMS: boolean;
  isRetryingWriteback: boolean;
  onRetryWritebackToPMS: () => void;
  onSaveNoteToPMS: () => void;
}

const getWritebackStatus = (
  selectedNote: ClinicalNote,
  patient: AppointmentPatient | undefined,
) => {
  const status = selectedNote?.writebackStatus;

  if (!patient?.pmsPatientId) return 'no-patient';
  if (!status?.clinicalNoteId) return 'can-save';
  if (status?.clinicalNoteId && status?.errorCode !== null) return 'failed';
  if (status?.clinicalNoteId && !status.processedAt) return 'processing';
  if (status?.isProcessed && status?.processedAt && !status.errorCode)
    return 'saved';
};

export const PMSNoteActions = ({
  patient,
  selectedNote,
  isSavingNoteToPMS,
  isRetryingWriteback,
  onSaveNoteToPMS,
  onRetryWritebackToPMS,
}: PMSNoteActionsProps) => {
  switch (getWritebackStatus(selectedNote, patient)) {
    case 'no-patient':
      return (
        <TooltipButton
          icon={faCloud}
          tooltip="Cannot sync this note. No matching patient found in your PMS."
          className={`${buttonStyling} vai-bg-gray-400 hover:vai-bg-gray-400 text-white`}
          disabled
        >
          Save to Chart
        </TooltipButton>
      );
    case 'can-save':
      return (
        <Button
          className={`${buttonStyling} hover:vai-ring-primary/50`}
          onClick={onSaveNoteToPMS}
          disabled={isSavingNoteToPMS}
        >
          <span>
            <FontAwesomeIcon icon={faCloud} />
          </span>
          {isSavingNoteToPMS ? 'Saving...' : 'Save to Chart'}
        </Button>
      );
    case 'processing':
      return (
        <Button className={`${buttonStyling}`} disabled>
          <span>
            <FontAwesomeIcon icon={faSpinner} className="vai-animate-spin" />
          </span>
          Saving to Chart...
        </Button>
      );
    case 'failed':
      return (
        <Button
          className={`${buttonStyling} !vai-bg-red-500 hover:!vai-bg-red-600 !vai-text-white`}
          onClick={onRetryWritebackToPMS}
          disabled={isRetryingWriteback}
        >
          <span>
            <FontAwesomeIcon icon={faExclamationTriangle} />
          </span>
          {isRetryingWriteback ? 'Retrying... ' : 'Sync failed, retry'}
        </Button>
      );
    case 'saved':
      return (
        <Button
          className={`${buttonStyling} !vai-bg-green-700 hover:!vai-bg-green-700/90 !vai-text-white`}
          disabled
        >
          <span>
            <FontAwesomeIcon icon={faCheckCircle} />
          </span>
          Saved to Chart
        </Button>
      );
  }
};
